import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import { NumberInput, Select } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  useCheckIfCreditAccountsBelongToTheSameTeamQuery,
  useGetModalTransferCreditDependenciesQuery,
  useGetProjectBudgetItemsQuery,
  useTransferCreditMutation,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { currency } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Store } from "../Store";
import { ModalConfirm } from "./ModalConfirm";

interface ModalTransferCreditProps {
  brandCreditAccountId: string;
  outgoingInvoiceId?: string;
  isOpen: boolean;
  onClose: () => void;
  onTransfer?: () => void;
}

export const ModalTransferCredit = ({
  brandCreditAccountId,
  outgoingInvoiceId,
  isOpen,
  onClose,
  onTransfer,
}: ModalTransferCreditProps) => {
  const appStore = useStore();
  const [store] = useState(() => new Store());

  const { loading: loadingDependencies } =
    useGetModalTransferCreditDependenciesQuery({
      variables: { id: brandCreditAccountId },
      skip: !brandCreditAccountId || !isOpen,
      onCompleted(data) {
        if (data?.brandCreditAccount) {
          store.setBrandCreditAccount(data.brandCreditAccount);
        }
      },
      ...onError(),
    });

  const { loading: loadingBudgetItems } = useGetProjectBudgetItemsQuery({
    variables: { id: store.form.project_id.value },
    skip: !store.form.project_id.value || !isOpen,
    onCompleted(data) {
      if (data.project) store.setProject(data.project);
    },
    ...onError(),
  });

  const { loading: loadingTeamsValidation } =
    useCheckIfCreditAccountsBelongToTheSameTeamQuery({
      variables: {
        credit_account_ids: [
          store.brandCreditAccount?.creditAccount.id!,
          store.form.to_credit_account_id.value,
        ],
      },
      skip:
        !store.brandCreditAccount ||
        !store.form.to_credit_account_id.value ||
        !isOpen,
      onCompleted(data) {
        store.requestToConfirmTransfer =
          !data.checkIfCreditAccountsBelongToTheSameTeam;
        store.requestConfirmed = false;
      },
      ...onError(),
    });

  const [transferCredit, { loading: loadingTransferCredit }] =
    useTransferCreditMutation({
      onCompleted(data) {
        if (data?.transferCredit) {
          appStore.UIStore.toast({
            title: t`Credit was successfully moved`,
            status: "success",
          });
          onCloseWrapper();
          onTransfer?.();
        }
      },
      ...onError(),
    });

  const onSubmit = async () => {
    if (!store.brandCreditAccount) return;
    if ((await store.form.form.validate()).hasError) return;

    if (store.requestToConfirmTransfer && !store.requestConfirmed) {
      modalConfirm.onOpen();
    } else {
      await transferCredit({
        variables: {
          from_credit_account_id: store.brandCreditAccount?.creditAccount.id,
          outgoing_invoice_id: outgoingInvoiceId,
          ...store.form.payload,
        },
      });
    }
  };

  const onCloseWrapper = () => {
    onClose();
    store.form.reset();
  };

  const modalConfirm = useDisclosure();
  const onConfirm = () => {
    store.requestConfirmed = true;
    onSubmit();
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen && !modalConfirm.isOpen}
        onClose={onCloseWrapper}
        size="2xl"
        trapFocus={false}
      >
        <ModalOverlay />

        {(loadingDependencies || loadingBudgetItems) && (
          <Center
            pos="absolute"
            zIndex={100}
            top="0"
            right="0"
            bottom="0"
            left="0"
            bg="white"
          >
            <Spinner color="teal.500" size="lg" />
          </Center>
        )}
        <ModalContent>
          <ModalHeader>
            <Trans>Transfer credit</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {store.brandCreditAccount && (
              <Stack spacing="6">
                <HStack>
                  <span>
                    <Trans>Remaining credit</Trans>:
                  </span>
                  <span className="font-bold text-2xl ml-2">
                    {currency.formatByWorkspace(
                      store.brandCreditAccount.stats.remaining_credit,
                    )}
                  </span>
                </HStack>
                <div className="pb-48">
                  <div className="mb-4">
                    <Select
                      asPortal
                      id="field-transfer-type"
                      {...fieldToSelectProps(
                        store.form.type,
                        store.typeOptions,
                      )}
                      placeholder={t`Select transfer location`}
                    />
                  </div>
                  {store.form.type.$ === "project" && (
                    <div className="mb-4">
                      <Select
                        asPortal
                        id="select-project"
                        {...fieldToSelectProps(
                          store.form.project_id,
                          store.projectOptions,
                        )}
                        placeholder={t`Select project`}
                      />
                    </div>
                  )}
                  {store.form.type.$ === "account" && (
                    <div className="mb-4">
                      <Select
                        asPortal
                        id="select-credit-account"
                        {...fieldToSelectProps(
                          store.form.to_credit_account_id,
                          store.creditAccountOptions,
                        )}
                        placeholder={t`Select credit account`}
                      />
                    </div>
                  )}
                  {store.form.type.$ === "project" &&
                    !!store.form.project_id.$ && (
                      <div className="mb-4">
                        <Select
                          asPortal
                          id="select-budget-item"
                          {...fieldToSelectProps(
                            store.form.to_credit_account_id,
                            store.budgetItemOptions,
                          )}
                          placeholder={t`Select budget item`}
                        />
                      </div>
                    )}
                  <div className="mb-4">
                    <NumberInput
                      currency
                      id="field-amount"
                      suffix={appStore.workspaceStore.settings?.currency.code}
                      {...fieldNumberModelToInputProps(store.form.amount)}
                    />
                  </div>
                </div>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="grey"
              onClick={onCloseWrapper}
              variant="outline"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              ml={3}
              isLoading={loadingTransferCredit || loadingTeamsValidation}
              onClick={onSubmit}
            >
              <Trans>Transfer credit</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ModalConfirm {...modalConfirm} onConfirm={onConfirm} />
    </React.Fragment>
  );
};

export default observer(ModalTransferCredit);
